import React, { useCallback, forwardRef, useMemo } from 'react'
import { Controller } from 'react-hook-form'
import PropTypes from 'prop-types'

import TextField from '@mui/material/TextField'

// Utility wrapper around @mui/material/TextField
const HookedTextField = forwardRef(
  (
    {
      name,
      inputProps,
      slotProps,
      InputProps,
      InputLabelProps,
      FormHelperTextProps,
      ...textFieldProps
    },
    ref
  ) => {
    const mergedSlotProps = useMemo(
      () => ({
        ...slotProps,
        input: {
          ...(slotProps?.input || {}),
          ...(InputProps || {})
        },
        htmlInput: {
          ...(slotProps?.htmlInput || {}),
          ...(inputProps || {})
        },
        inputLabel: {
          ...(slotProps?.inputLabel || {}),
          ...(InputLabelProps || {})
        },
        formHelperText: {
          ...(slotProps?.formHelperText || {}),
          ...(FormHelperTextProps || {})
        }
      }),
      [slotProps, inputProps, InputProps, InputLabelProps, FormHelperTextProps]
    )

    const renderInput = useCallback(
      ({ field, fieldState: { error } }) => {
        const { value, ...fieldWithoutValue } = field
        const usesNativeInputValue =
          !textFieldProps.select &&
          (textFieldProps.type === undefined ||
            ['text', 'search', 'url', 'tel', 'email', 'password', 'number'].includes(textFieldProps.type))
        const safeValue =
          usesNativeInputValue && (value === null || value === undefined) ? '' : value

        return (
          <TextField
            error={!!error}
            margin='normal'
            variant='standard'
            {...textFieldProps}
            slotProps={mergedSlotProps}
            helperText={error?.message}
            {...fieldWithoutValue}
            value={safeValue}
            ref={ref}
          />
        )
      },
      [textFieldProps, mergedSlotProps, ref]
    )

    return (
      <Controller
        name={name}
        render={renderInput}
      />
    )
  }
)

HookedTextField.propTypes = {
  /** Field name **/
  name: PropTypes.string.isRequired
  /** @param textFieldProps {restProp}: Props for customizing the underlying @mui/material/TextField.
   * The 'value', 'onChange', 'onBlur', 'ref', 'label', 'helperText' props can't be overridden. **/
}

export default HookedTextField
